// @ts-nocheck
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Container,
  Card,
  IconButton,
  useMediaQuery,
  Chip,
  alpha
} from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import { useLocations } from '../hooks/useLocations';
import { useTechnicians } from '../hooks/useTechnicians';
import { useTheme } from '@mui/material/styles';
import { getSchedule, addScheduleEvent, updateScheduleEvent, deleteScheduleEvent } from '../services/supabaseService';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import TodayIcon from '@mui/icons-material/Today';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { styled } from '@mui/material/styles';

// Custom CSS for FullCalendar
import './Schedule.css';

const SectionHeader = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(4),
  background: theme.palette.mode === 'dark' ? '#424242' : theme.palette.primary.main,
  color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.contrastText,
  borderRadius: theme.shape.borderRadius,
}));

const Schedule: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedEvent, setSelectedEvent] = useState<any>({
    start: '',
    end_date: '',
    assignee: '',
    location: '',
    status: 'Scheduled',
    note: '',
  });
  const [events, setEvents] = useState<any[]>([]);
  const [showMonthPicker, setShowMonthPicker] = useState<boolean>(false);
  const calendarRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOpen, setFilterOpen] = useState<boolean>(false); // Added for filter dialog

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const buttonTextColor = theme.palette.mode === 'dark' ? 'white' : 'black';
  const primaryColor = theme.palette.primary.main;

  const locations = useLocations();
  const assignees = useTechnicians();
  const statuses = ['Scheduled', 'Completed'];

  const tableHeadStyle = theme.palette.mode === 'dark' ? {
    backgroundColor: '#363636',
    '& .MuiTableCell-head': {
      color: '#ffffff',
    }
  } : {};

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.updateSize();
    }
  }, [showMonthPicker]);

  const fetchEvents = async () => {
    try {
      const scheduleData = await getSchedule();
      // Map the end_date field to the end field expected by FullCalendar
      // and ensure we're only using date parts (no timestamps)
      const mappedEvents = scheduleData.map(event => {
        // Format dates to only include the date part (YYYY-MM-DD)
        const startDate = new Date(event.start).toISOString().split('T')[0];
        const endDate = event.end_date ? new Date(event.end_date).toISOString().split('T')[0] : startDate;

        return {
          ...event,
          start: startDate,
          end_date: endDate,
          end: endDate // Map end_date to end for FullCalendar
        };
      });

      const sortedEvents = mappedEvents.sort((a, b) =>
        new Date(b.start).getTime() - new Date(a.start).getTime()
      );
      setEvents(sortedEvents);
    } catch (error) {
      console.error("Failed to fetch schedule events:", error);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleDateClick = (arg) => {
    setSelectedEvent({
      start: arg.dateStr,
      end_date: arg.dateStr,
      assignee: '',
      location: '',
      status: 'Scheduled',
    });
    setIsOpen(true);
  };

  const handleEventClick = (clickInfo) => {
    // Format dates to only include the date part (YYYY-MM-DD)
    const startDate = clickInfo.event.start ? clickInfo.event.start.toISOString().split('T')[0] : '';
    const endDate = clickInfo.event.end ? clickInfo.event.end.toISOString().split('T')[0] : startDate;

    setSelectedEvent({
      ...selectedEvent,
      title: clickInfo.event.title,
      start: startDate,
      end_date: endDate,
      id: clickInfo.event.id,
      assignee: clickInfo.event.extendedProps.assignee,
      location: clickInfo.event.extendedProps.location,
      status: clickInfo.event.extendedProps.status,
      note: clickInfo.event.extendedProps.note,
    });
    setIsOpen(true);
  };

  const handleEventDelete = async () => {
    try {
      await deleteScheduleEvent(selectedEvent.id);
      setEvents(events.filter((event) => event.id !== selectedEvent.id));
      setIsOpen(false);
    } catch (error) {
      console.error("Failed to delete event:", error);
      alert('Failed to delete event. Please try again.');
    }
  };

  const handleEventAddOrUpdate = async (e) => {
    e.preventDefault();

    // Ensure we're only using the date part (YYYY-MM-DD)
    const startDate = selectedEvent.start;
    const endDate = selectedEvent.end_date || selectedEvent.start;

    // Calculate quarter based on the start date (3 quarters per year)
    const eventDate = new Date(startDate);
    const month = eventDate.getMonth();
    const quarter = Math.floor(month / 4) + 1; // Split year into 3 quarters (0-3, 4-7, 8-11)

    const eventData = {
      title: `Q${quarter} - ${selectedEvent.location}`, // Simplified title format
      start: startDate,
      end_date: endDate,
      location: selectedEvent.location,
      assignee: selectedEvent.assignee,
      status: selectedEvent.status,
      note: selectedEvent.note || '',
    };

    try {
      if (selectedEvent.id) {
        // Update existing event
        await updateScheduleEvent(selectedEvent.id, eventData);
        setEvents(
          events.map((event) =>
            event.id === selectedEvent.id ? { ...eventData, id: selectedEvent.id, end: eventData.end_date } : event
          )
        );
      } else {
        // Add new event
        const newEvent = await addScheduleEvent(eventData);

        // Check if newEvent exists and has the expected structure
        if (newEvent && Array.isArray(newEvent) && newEvent.length > 0 && newEvent[0].id) {
          setEvents([...events, { ...eventData, id: newEvent[0].id, end: eventData.end_date }]);
        } else if (newEvent && typeof newEvent === 'object' && newEvent.id) {
          // Handle case where newEvent might be a single object with an id
          setEvents([...events, { ...eventData, id: newEvent.id, end: eventData.end_date }]);
        } else {

          // Refresh events from the server
          const refreshedEvents = await getSchedule();
          const mappedEvents = refreshedEvents.map(event => {
            const startDate = new Date(event.start).toISOString().split('T')[0];
            const endDate = event.end_date ? new Date(event.end_date).toISOString().split('T')[0] : startDate;

            return {
              ...event,
              start: startDate,
              end_date: endDate,
              end: endDate
            };
          });
          setEvents(mappedEvents);
        }
      }
      setIsOpen(false);
    } catch (error) {
      console.error("Failed to save event:", error);
      alert('Failed to save event. Please try again.');
    }
  };

  const handleLocationChange = (e) => {
    setSelectedEvent(prev => ({ ...prev, location: e.target.value }));
  };

  const handleStatusChange = (e) => {
    setSelectedEvent(prev => ({ ...prev, status: e.target.value }));
  };

  const handleAssigneeChange = (e) => {
    setSelectedEvent(prev => ({ ...prev, assignee: e.target.value }));
  };

  const renderEventContent = (eventInfo) => {
    const { extendedProps } = eventInfo.event;
    // Calculate quarter based on the event's start date (3 quarters per year)
    const eventDate = new Date(eventInfo.event.start);
    const month = eventDate.getMonth();
    const quarter = Math.floor(month / 4) + 1; // Split year into 3 quarters (0-3, 4-7, 8-11)

    // Determine status color
    const statusColor = extendedProps.status === 'Completed' ? '#4caf50' : '#ff9800';

    return (
      <Box sx={{
        p: 0.5,
        borderRadius: 1,
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}>
        <Typography variant="subtitle2" noWrap fontWeight="bold">
          Q{quarter} - {extendedProps.location}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
          <Typography variant="caption" noWrap sx={{ flex: 1 }}>
            {extendedProps.assignee}
          </Typography>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: statusColor,
              ml: 0.5
            }}
          />
        </Box>
      </Box>
    );
  };

  const customButtons = {
    addEvent: {
      text: 'Add Event',
      click: () => {
        const today = new Date().toISOString().split('T')[0];
        setSelectedEvent({
          start: today,
          end_date: today,
          assignee: '',
          location: '',
          status: 'Scheduled',
          note: '',
        });
        setIsOpen(true);
      }
    }
  };

  const [filter, setFilter] = useState({
    location: '',
    quarter: '',
    assignee: '',
    status: ''
  });

  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  const [isMonthPickerOpen, setIsMonthPickerOpen] = useState<boolean>(false);
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());

  const handleMonthSelect = (month: number) => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      const date = new Date();
      date.setMonth(month);
      calendarApi.gotoDate(date);
    }
    setIsMonthPickerOpen(false);
  };

  const filteredEvents = events.filter((event) =>
    (!filter.location || event.location === filter.location) &&
    (!filter.quarter || `Q${Math.floor((new Date(event.start).getMonth() / 4) + 1)}` === filter.quarter) &&
    (!filter.assignee || event.assignee === filter.assignee) &&
    (!filter.status || event.status === filter.status)
  );
  const searchFilteredEvents = filteredEvents.filter(event =>
    event.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.assignee?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.status?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const emptyRows = rowsPerPage - Math.min(rowsPerPage, searchFilteredEvents.length - page * rowsPerPage);

  const startIndex = page * rowsPerPage;
  const paginatedEvents = searchFilteredEvents.slice(startIndex, startIndex + rowsPerPage);

  // Determine the initial view based on screen size
  const getInitialView = () => {
    if (isMobile) return 'listMonth';
    if (isTablet) return 'dayGridWeek';
    return 'dayGridMonth';
  };

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="text.primary">
          Schedule
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
          Plan and manage quarterly machine inspections and maintenance schedules.
        </Typography>
      </Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            const today = new Date().toISOString().split('T')[0];
            setSelectedEvent({
              start: today,
              end_date: today,
              assignee: '',
              location: '',
              status: 'Scheduled',
              note: '',
            });
            setIsOpen(true);
          }}
        >
          Add Event
        </Button>
      </Box>

      <Card
        elevation={3}
        sx={{
          mb: 4,
          overflow: 'hidden',
          borderRadius: 2,
          '& .fc': { // Target FullCalendar root
            '--fc-border-color': theme.palette.divider,
            '--fc-button-text-color': theme.palette.primary.contrastText,
            '--fc-button-bg-color': theme.palette.primary.main,
            '--fc-button-border-color': theme.palette.divider,
            '--fc-button-hover-bg-color': theme.palette.primary.dark,
            '--fc-button-hover-border-color': theme.palette.primary.main,
            '--fc-button-active-bg-color': theme.palette.primary.main,
            '--fc-button-active-border-color': theme.palette.primary.dark,
            '--fc-event-bg-color': theme.palette.primary.main,
            '--fc-event-border-color': theme.palette.primary.dark,
            '--fc-event-text-color': theme.palette.primary.contrastText,
            '--fc-event-selected-overlay-color': theme.palette.primary.light,
            '--fc-page-bg-color': theme.palette.background.paper,
            '--fc-neutral-bg-color': theme.palette.background.default,
            '--fc-neutral-text-color': theme.palette.text.primary,
            '--fc-today-bg-color': alpha(theme.palette.primary.main, 0.1),
          }
        }}
      >
        <Box sx={{ height: { xs: '60vh', md: '70vh' }, p: { xs: 1, md: 2 } }}>
          <FullCalendar
            plugins={[dayGridPlugin, interactionPlugin, timeGridPlugin, listPlugin]}
            initialView={getInitialView()}
            events={events}
            eventContent={renderEventContent}
            dateClick={handleDateClick}
            eventClick={handleEventClick}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,dayGridWeek,listMonth',
            }}
            buttonText={{
              today: 'Today',
              month: 'Month',
              week: 'Week',
              list: 'List'
            }}
            ref={calendarRef}
            height="100%"
            eventColor={theme.palette.primary.main}
            eventBorderColor={theme.palette.primary.dark}
            dayMaxEvents={isMobile ? 2 : 4}
            stickyHeaderDates={true}
            fixedWeekCount={false}
          />
        </Box>
      </Card>

      <Box sx={{ mb: 3 }}>
        <Paper
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.background.paper,
            boxShadow: theme.shadows[3]
          }}
          elevation={3}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField
              fullWidth
              placeholder="Search"
              variant="standard"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: theme.palette.text.secondary }} />,
                sx: {
                  color: theme.palette.text.primary,
                  '&::before': { borderBottomColor: theme.palette.divider },
                  '&::after': { borderBottomColor: theme.palette.primary.main },
                  '&:hover:not(.Mui-disabled)::before': { borderBottomColor: theme.palette.text.secondary }
                }
              }}
              sx={{
                '& .MuiInputBase-input::placeholder': { color: theme.palette.text.secondary },
                '& .MuiInputBase-input': { color: theme.palette.text.primary }
              }}
            />
            <Button
              variant="contained"
              onClick={() => setFilterDialogOpen(true)}
              startIcon={<FilterListIcon />}
              sx={{
                minWidth: { xs: '100%', sm: 'auto' }
              }}
            >
              FILTER
            </Button>
          </Stack>
        </Paper>
      </Box>

      <Paper
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[3]
        }}
        elevation={3}
      >
        <TableContainer sx={{ maxHeight: 'none' }}>
          <Table aria-label="events table" stickyHeader>
            <TableHead sx={tableHeadStyle}>
              <TableRow>
                <TableCell>Quarter</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Assignee</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Note</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedEvents.length > 0 ? (
                paginatedEvents.map((event) => (
                  <TableRow key={event.id} hover>
                    <TableCell>{`Q${Math.floor((new Date(event.start).getMonth() / 4) + 1)}`}</TableCell>
                    <TableCell>
                      {new Date(event.start).toLocaleDateString()}
                      {event.end_date && event.end_date !== event.start &&
                        ` to ${new Date(event.end_date).toLocaleDateString()}`}
                    </TableCell>
                    <TableCell>{event.assignee}</TableCell>
                    <TableCell>{event.location}</TableCell>
                    <TableCell>
                      <Chip
                        label={event.status}
                        color={event.status === 'Completed' ? 'success' : 'warning'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {event.note}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          setSelectedEvent(event);
                          setIsOpen(true); // Open the modal for editing
                        }}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      No events found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={searchFilteredEvents.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Paper>

      {/* Event Dialog */}
      <Dialog open={isOpen} onClose={() => setIsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ mb: 2 }}>{selectedEvent.id ? 'Edit Event' : 'Add New Event'}</DialogTitle>
        <DialogContent>
          <Box component="form" noValidate autoComplete="off" onSubmit={handleEventAddOrUpdate} sx={{ pt: 2 }}>
            <Stack spacing={3}>
              <TextField
                label="Date"
                type="date"
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    mt: 1 // Keep only the margin top to push label down
                  }
                }}
                value={selectedEvent.start}
                onChange={(e) => setSelectedEvent(prev => ({ ...prev, start: e.target.value }))}
                fullWidth
                sx={{
                  mt: 2 // Keep only the margin top to push the entire field down
                }}
              />
              <TextField
                label="End Date"
                type="date"
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    mt: 1 // Keep only the margin top to push label down
                  }
                }}
                value={selectedEvent.end_date || selectedEvent.start}
                onChange={(e) => setSelectedEvent(prev => ({ ...prev, end_date: e.target.value }))}
                fullWidth
                sx={{
                  mt: 2 // Keep only the margin top to push the entire field down
                }}
              />
              <FormControl fullWidth>
                <InputLabel>Assignee</InputLabel>
                <Select value={selectedEvent.assignee} onChange={handleAssigneeChange}>
                  {assignees.map((assignee) => (
                    <MenuItem key={assignee} value={assignee}>{assignee}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Location</InputLabel>
                <Select value={selectedEvent.location} onChange={handleLocationChange}>
                  {locations.map((loc, index) => (
                    <MenuItem key={index} value={loc}>{loc}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={selectedEvent.status} onChange={handleStatusChange}>
                  {statuses.map((status) => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Note"
                value={selectedEvent.note}
                onChange={(e) => setSelectedEvent(prev => ({ ...prev, note: e.target.value }))}
                multiline
                rows={4}
                fullWidth
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button color="inherit" onClick={() => setIsOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEventAddOrUpdate}>Save</Button>
          {selectedEvent.id && (
            <Button variant="outlined" color="error" onClick={handleEventDelete}>Delete</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Filter Dialog */}
      <Dialog open={filterDialogOpen} onClose={() => setFilterDialogOpen(false)} aria-labelledby="filter-dialog-title" maxWidth="sm" fullWidth={true}>
        <DialogTitle id="filter-dialog-title">Filter Events</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>Location</InputLabel>
            <Select
              value={filter.location}
              onChange={(e) => setFilter({ ...filter, location: e.target.value })}
              label="Location"
            >
              <MenuItem value="">All Locations</MenuItem>
              {locations.map((loc) => (
                <MenuItem key={loc} value={loc}>{loc}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Quarter</InputLabel>
            <Select
              value={filter.quarter}
              onChange={(e) => setFilter({ ...filter, quarter: e.target.value })}
              label="Quarter"
            >
              <MenuItem value="">All Quarters</MenuItem>
              {['Q1', 'Q2', 'Q3'].map((quarter) => (
                <MenuItem key={quarter} value={quarter}>{quarter}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Assignee</InputLabel>
            <Select
              value={filter.assignee}
              onChange={(e) => setFilter({ ...filter, assignee: e.target.value })}
              label="Assignee"
            >
              <MenuItem value="">All Assignees</MenuItem>
              {assignees.map((assignee) => (
                <MenuItem key={assignee} value={assignee}>{assignee}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Status</InputLabel>
            <Select
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              label="Status"
            >
              <MenuItem value="">All Statuses</MenuItem>
              {statuses.map((status) => (
                <MenuItem key={status} value={status}>{status}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setFilter({ location: '', quarter: '', assignee: '', status: '' })} color="inherit">
            Clear Filters
          </Button>
          <Button onClick={() => setFilterDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={() => setFilterDialogOpen(false)} variant="contained">
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Schedule;